import CloseIcon from '@mui/icons-material/Close';
import { Alert } from '@mui/lab';
import { Box, Link, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import RHFDevtoolsWrapper from 'components/rhf-devtools-wrapper/rhf-devtools-wrapper.component';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddToCartButtton from './add-to-cart-button/add-to-cart-button.component';
import ItemDescription from './item-description/item-description.component';
import ItemInstructionAdditionalText from './item-instruction-additional-text/item-instruction-additional-text.component';
import ItemInstructions from './item-instructions/item-instructions.component';
import ItemMediumImage from './item-medium-image/item-medium-image.component';
import ItemQtyPicker from './item-qty-picker/item-qty-picker.component';
import { useMenuModal } from './menu-modal.hooks';
import * as S from './menu-modal.styles';
import NutritionalInfoLink from './nutritional-info-link/nutritional-info-link.component';
import NutritionalInfo from './nutritional-info/nutritional-info.component';
import OptionGroupTabs from './option-group-tabs/option-group-tabs.component';

const MenuModal = ({ open }) => {
  const { t } = useTranslation();

  const { getValues, control, trigger, unregister } = useFormContext();

  const {
    handleClose,
    onClose,
    visibility,
    onError,
    onSubmit,
    handleTabChange,
    filteredEditOptionsByGroupId,
    tabVal,
    extendedAttributes,
    mediumImage,
    title,
    shortDescription,
    longDescription,
    item,
    selectionGroups,
    isCustomizations,
    tabValues,
    customizationGroups,
    editInfo,
    catalog,
  } = useMenuModal({ getValues, trigger });

  useEffect(() => {
    return () => unregister();
  }, [unregister]);

  return (
    <S.CustomDialog
      open={open}
      scroll="paper"
      fullWidth
      onClose={onClose}
      PaperProps={{ 'data-testid': 'CatalogModal.CustomDialog' }}
      aria-labelledby={`dialog-title-${item?.itemId}`}
    >
      <S.Close
        data-testid="CustomDialog.Close"
        onClick={handleClose}
        aria-label={t('dialogs.close.ariaLabel')}
      >
        <CloseIcon />
      </S.Close>

      <S.Content id="CatalogModal.Content">
        <ItemMediumImage mediumImage={mediumImage} />

        {!visibility.visible && (
          <Box m={1}>
            <Alert data-testid="MenuModal.Alert.Visibility" severity="warning">
              {t('catalog.modal.notVisible')}
            </Alert>
          </Box>
        )}

        <NutritionalInfo extendedAttributes={extendedAttributes} />

        <Box mb="2em" mt="2em">
          <ItemDescription
            title={title}
            shortDescription={shortDescription}
            longDescription={longDescription}
            itemId={item?.itemId}
            catalog={catalog}
            calories={item?.calories}
          />

          <OptionGroupTabs
            selectionGroups={selectionGroups}
            isCustomizations={isCustomizations}
            tabVal={tabVal}
            handleTabChange={handleTabChange}
            tabValues={tabValues}
            filteredEditOptionsByGroupId={filteredEditOptionsByGroupId}
            editInfo={editInfo}
            customizationGroups={customizationGroups}
          />

          <ItemInstructions editInfo={editInfo} />

          <ItemInstructionAdditionalText />
        </Box>

        <NutritionalInfoLink extendedAttributes={extendedAttributes} />

        {catalog?.extendedAttributes?.NUTRITIONAL_INFO_LINK && (
          <Box mx={2} mb={2} data-testid="MenuModal.NutritionalNotice">
            <Typography variant="body2" component="span">
              {t('catalog.modal.calories.notice')}{' '}
            </Typography>
            <Link
              href={catalog.extendedAttributes.NUTRITIONAL_INFO_LINK}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'text.primary',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
              data-testid="MenuModal.FullNutritionalLink"
            >
              {t('catalog.modal.calories.fullNutritionalInfo')}
            </Link>
          </Box>
        )}
      </S.Content>

      <S.CartActions>
        <Grid container>
          <ItemQtyPicker title={title} />

          <AddToCartButtton
            onSubmit={onSubmit}
            onError={onError}
            title={title}
          />
        </Grid>
      </S.CartActions>

      <RHFDevtoolsWrapper control={control} />
    </S.CustomDialog>
  );
};

export default MenuModal;
